import styled from "@emotion/styled";
import React, { useState } from "react";

import Link from "../../components/Link";
import Modal from "../../components/Modal";
import { COOKIE_SKIP_INTRO } from "../../constants";
import { deleteCookie } from "../../utils/cookie";
import StoreHeader from "../../components/StoreHeader";

type FaqItem = {
  id: string;
  question: string;
  answer: string;
};

type Props = {
  amount: number;
  storeLogo: string;
  storeName: string;
  faqList: FaqItem[];
};

const StyledModal = styled(Modal)`
  header {
    background: #f6f1f1;
    flex-basis: 10rem;
    flex-shrink: 0;
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }

  main {
    padding: 0;
  }

  footer {
    align-items: center;
    justify-content: center;
  }
`;

const Logo = styled.img`
  height: 24px;
`;

const MainContentContainer = styled.div`
  padding: 1.5rem 1.5rem 0;
  flex: 1 0 0;
  overflow-y: scroll;
`;

const TitleText = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  margin-top: 0;
  margin-bottom: 1.5rem;
`;

const Details = styled.details`
  margin: 0 -1.5rem;

  summary {
    cursor: pointer;
    list-style-type: none;
    position: relative;
    padding: 1.5rem;
    border-top: 1px solid #e1e1e1;

    & > h2 {
      display: inline-block;
      font-size: 1rem;
      font-weight: 400;
      margin: 0 3rem 0 0;
    }
  }

  summary::-webkit-details-marker {
    display: none;
  }

  summary::after {
    content: "";
    background: url("/assets/chevron-down.png") no-repeat;
    background-size: cover;
    display: inline-block;
    width: 1.5rem;
    height: 1.5rem;
    position: absolute;
    right: 1.5rem;
  }

  &[open] summary {
    border-bottom: 1px solid #e1e1e1;

    &::after {
      transform: rotate(180deg);
    }
  }

  &:last-of-type {
    border-bottom: 1px solid #e1e1e1;
  }

  & > div {
    margin: 0;
    padding: 1.5rem;
    font-size: 0.75rem;
    background: #f4f4f4;

    ol,
    ul {
      padding-left: 1rem;
      list-style-position: outside;
      margin: 0;
    }

    ul {
      list-style-type: "・";
    }

    p:first-child {
      margin-top: 0;
    }

    p:last-child {
      margin-bottom: 0;
    }
  }
`;

const ShowAllLink = styled(Link)`
  display: inline-block;
  margin: 1.5rem 0;
  align-self: flex-start;
`;

const StyledLink = styled(Link)`
  font-size: 0.75rem;
`;

const Help = (props: Partial<Props>) => {
  const [showAll, setShowAll] = useState(false);
  const faqList = props.faqList || [];
  const initialCount = 3;
  const visibleFaq = showAll ? faqList : faqList.slice(0, initialCount);

  const goBack = () => {
    const referrerBaseURL = document.referrer.split("?")[0];
    const originURL = window.location.origin;
    // Only when coming from Introduction screen
    // Since Introduction is automatically skipped, we need to delete to cookie first
    if (referrerBaseURL === originURL || referrerBaseURL === `${originURL}/`) {
      deleteCookie(COOKIE_SKIP_INTRO);
    }

    history.back();
  };

  return (
    <StyledModal
      onBackButtonClick={() => {
        goBack();
      }}
      headerContent={
        <StoreHeader storeName={props.storeName} amount={props.amount} storeLogo={props.storeLogo} />
      }
      navigatorContent={<Logo src="/assets/paidy.png" alt="Paidy logo" width={61} height={24} />}
      footerContent={
        <StyledLink
          href="#"
          title="戻る"
          onClick={(event) => {
            event.preventDefault();
            goBack();
          }}
          id="back-link"
          testId="help-back-link"
        >
          戻る
        </StyledLink>
      }
      id="modal"
      testId="help-modal"
    >
      <MainContentContainer>
        <TitleText>よくあるご質問</TitleText>
        {visibleFaq.map((faq) => (
          <Details key={faq.id}>
            <summary>
              <h2>{faq.question}</h2>
            </summary>
            <div dangerouslySetInnerHTML={{ __html: faq.answer }} />
          </Details>
        ))}
        {!showAll && faqList.length > initialCount && (
          <ShowAllLink
            href="#"
            title="その他のご質問はこちら"
            onClick={(event) => {
              event.preventDefault();
              setShowAll(true);
            }}
            id="show-all-link"
            testId="help-show-all-link"
          >
            その他のご質問はこちら
          </ShowAllLink>
        )}
      </MainContentContainer>
    </StyledModal>
  );
};

export default Help;
